import {
  RecommendationType,
  AssetType,
  OperatedType,
  ThemesAssetsRecommendationData,
  AssetsRecommendationData,
  DisplayAdsRecommendationData,
} from '../../types';
import { CopilotRecommendationService } from './CopilotRecommendationService';
import { AssetTypeRequestMapping, OperatedTypeRequestMapping } from './constants';
import {
  mapThemeAsset,
  mapAssetRecommendation,
  mapToUserPrompt,
  mapToThemes,
  mapToAsset,
  mapDisplayAdsRecommendation,
} from './utils';
import { ICopilotRepository, ICopilotRepositoryProps } from './types';
import { pharmaCustomerIdsFromCustomerExclusionList } from './pharma-customer-ids-from-customer-exclusion-list';

const DEFAULT_MAX_COUNT_THEMES = 4; // default 4, max 6, min 3
const DEFAULT_MAX_ADS_COUNT = 10; // default 10

/**
 * Contains the logic for making the odata calls required to fetch recommendations. This repository will be automatically created by
 * CopilotRepositoryProvider, so it is recommended you get the context value instead of manually creating this repository.
 * @param {Object} options.serverContext - Server context with current scope and small token.
 * @param {Number} [options.campaignId] - The campaign ID to fetch recommendations for.
 * @returns - Repository object for fetching campaign asset recommendations.
 */
export const CopilotRepository = ({
  serverContext, campaignId, enableLogoAndCTA, useClipchamp,
}: ICopilotRepositoryProps): ICopilotRepository => {
  const recommendationService = CopilotRecommendationService({ serverContext });

  const getThemeAssetsRecommendation = (
    finalUrl: string,
    prompts: string[],
    maxCount: number = DEFAULT_MAX_COUNT_THEMES,
  ) => {
    const payload = {
      GetThemeAssetsRecommendationRequest: {
        '@odata.type': '#Model.GetThemeAssetsRecommendationRequest',
        FinalURL: finalUrl,
        UserPrompt: {
          '@odata.type': '#Model.AssetRecommendationUserPrompt',
          Prompts: prompts,
        },
        MaxCountThemes: maxCount,
      },
      UseSync: true,
    };

    let methodName = 'GetThemeAssetsRecommendation';

    if (enableLogoAndCTA) {
      methodName = 'GetThemeAssetsRecommendation?enableLogo=true&enableCTA=true';
    }
    return recommendationService.getRecommendation(campaignId, methodName, payload);
  };

  const getMappedThemeAssetsRecommendation = (
    finalUrl: string,
    prompts: string[],
    maxCount?: number,
  ) => new Promise<ThemesAssetsRecommendationData>((resolve, reject) => {
    getThemeAssetsRecommendation(finalUrl, prompts, maxCount)
      .then(result => resolve(mapThemeAsset(result)))
      .catch(err => reject(err));
  });

  const getAssetsRecommendation = (
    finalUrl: string,
    recommendationType: RecommendationType,
    prompts: string[] = [],
    themes: string[] = [],
    maxCountThemes: number = DEFAULT_MAX_COUNT_THEMES,
  ) => {
    const payload = {
      GetAssetsRecommendationRequest: {
        '@odata.type': '#Model.GetAssetsRecommendationRequest',
        FinalURL: finalUrl,
        RecommendationType: recommendationType,
        UserPrompt: mapToUserPrompt(prompts),
        Themes: mapToThemes(themes),
        MaxCountThemes: maxCountThemes, // default 4, max 6, min 3
        ...(recommendationType === RecommendationType.LinkedIn && {
          MaxLengthShortHeadline: 200,
          MaxLengthDescription: 600,
        }),
      },
      UseSync: true,
    };

    let methodName = 'GetAssetsRecommendation';

    if (enableLogoAndCTA) {
      methodName = 'GetAssetsRecommendation?enableLogo=true&enableCTA=true';
    }
    return recommendationService.getRecommendation(campaignId, methodName, payload);
  };

  const getMappedAssetsRecommendation = (
    finalUrl: string,
    recommendationType: RecommendationType,
    prompts?: string[],
    themes?: string[],
    maxCountThemes?: number,
  ) => new Promise<AssetsRecommendationData>((resolve, reject) => {
    getAssetsRecommendation(finalUrl, recommendationType, prompts, themes, maxCountThemes)
      .then(result => resolve(mapAssetRecommendation(result)))
      .catch(err => reject(err));
  });

  const refineTextAsset = (
    finalUrl: string,
    assetType: AssetType,
    operatedType: OperatedType,
    shortHeadlines: string[],
    longHeadlines: string[],
    descriptions: string[],
    selectedIndex: number,
    prompts: string[] = [],
    tones?: string[],
  ) => {
    const payload = {
      RefineTextAssetRequest: {
        '@odata.type': '#Model.RefineTextAssetRequest',
        FinalURL: finalUrl,
        AssetType: AssetTypeRequestMapping[assetType],
        OperatedType: OperatedTypeRequestMapping[operatedType],
        UserPrompt: mapToUserPrompt(prompts),
        Tones: tones,
        ExistingAsset: mapToAsset(shortHeadlines, longHeadlines, descriptions),
        SelectedIndex: selectedIndex,
      },
      UseSync: true,
    };
    return recommendationService.getRecommendation(campaignId, 'RefineTextAsset', payload);
  };

  const rewriteTextAsset = (
    finalUrl: string,
    assetType: AssetType,
    shortHeadlines: string[],
    longHeadlines: string[],
    descriptions: string[],
    selectedIndex: number,
    prompts: string[],
  ) => refineTextAsset(
    finalUrl,
    assetType,
    OperatedType.TextRewriteWithDescription,
    shortHeadlines,
    longHeadlines,
    descriptions,
    selectedIndex,
    prompts,
  );

  const tryDifferentToneTextAsset = (
    finalUrl: string,
    assetType: AssetType,
    shortHeadlines: string[],
    longHeadlines: string[],
    descriptions: string[],
    selectedIndex: number,
    tones: string[],
  ) => refineTextAsset(
    finalUrl,
    assetType,
    OperatedType.TextRewriteWithTone,
    shortHeadlines,
    longHeadlines,
    descriptions,
    selectedIndex,
    undefined,
    tones,
  );

  const findSimilarTextAsset = (
    finalUrl: string,
    assetType: AssetType,
    shortHeadlines: string[],
    longHeadlines: string[],
    descriptions: string[],
    selectedIndex: number,
  ) => refineTextAsset(
    finalUrl,
    assetType,
    OperatedType.TextFindSimilarText,
    shortHeadlines,
    longHeadlines,
    descriptions,
    selectedIndex,
  );

  const generateImages = (
    prompts: string[],
    maxCount?: number,
  ) => {
    const payload = {
      GenerateImagesRequest: {
        '@odata.type': '#Model.GenerateImagesRequest',
        UserPrompt: {
          '@odata.type': '#Model.AssetRecommendationUserPrompt',
          Prompts: prompts,
        },
        MaxCountImage: maxCount, // default 4, max 5
      },
    };
    return recommendationService.getRecommendation(campaignId, 'GenerateImages', payload);
  };

  const refineImage = (
    finalUrl: string,
    imageUrl: string,
    prompts: string[],
    imageAssetId: string,
    base64StringImage: string,
    operationType: string = 'ImageChangeBackground',
    maxCount?: number,
  ) => {
    const payload = {
      RefineImageRequest: {
        '@odata.type': '#Model.RefineImageRequest',
        UserPrompt: {
          '@odata.type': '#Model.AssetRecommendationUserPrompt',
          Prompts: prompts,
        },
        FinalURL: finalUrl,
        OperationType: operationType,
        ImageAsset: {
          '@odata.type': '#Model.AssetRecommendationImageAsset',
          Url: imageUrl,
          ImageAssetID: imageAssetId,
          Base64StringImage: base64StringImage,
        },
        MaxCountImages: maxCount, // int, default 1, max 40
      },
      UseSync: true,
    };
    return recommendationService.getRecommendation(campaignId, 'RefineImage', payload);
  };

  const getDisplayAdsRecommendation = (
    finalUrl: string,
    maxAdsCount: number = DEFAULT_MAX_ADS_COUNT,
    brandKit?: any,
    templateVersion?: string,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const payload = {
      Url: finalUrl,
      Count: maxAdsCount, // optional, default 10.
      Mock: false,
      ...(brandKit && { BrandKit: brandKit }),
      ...(templateVersion && { TemplateVersion: templateVersion }),
    };

    return recommendationService.post(campaignId, 'CreateDisplayAdsRecommendation', payload);
  };

  const getMappedDisplayAdsRecommendation = (
    finalUrl: string,
    maxAdsCount: number = DEFAULT_MAX_ADS_COUNT,
    brandKit?: any,
    templateVersion?: string,
  ) => new Promise<DisplayAdsRecommendationData>((resolve, reject) => {
    getDisplayAdsRecommendation(finalUrl, maxAdsCount, brandKit, templateVersion)
      .then((result) => {
        if (result && result.data === null) {
          reject(result);
        }
        resolve(mapDisplayAdsRecommendation(result));
      })
      .catch(err => reject(err));
  });

  const editDisplayAdsRecommendation = (ads: any) => {
    const payload = {
      data: {
        ads: Array.isArray(ads) ? ads : [ads],
        Mock: false,
      },
    };

    return recommendationService.post(campaignId, 'EditDisplayAdsRecommendation', payload);
  };

  const editMappedDisplayAdsRecommendation = (ads: any) => new Promise<DisplayAdsRecommendationData>((resolve, reject) => {
    editDisplayAdsRecommendation(ads)
      .then((result) => {
        if (result && result.data === null) {
          reject(result);
        }
        resolve(mapDisplayAdsRecommendation(result));
      })
      .catch(err => reject(err));
  });

  const getDisplayAdsTemplateDefinition = (templateId: string) => {
    const payload = {
      TemplateId: templateId,
    };

    return recommendationService.post(campaignId, 'GetDisplayAdsTemplateDefinition', payload);
  };

  const getDisplayAdsTemplateGroups = () => recommendationService.post(campaignId, 'GetDisplayAdsTemplateGroups', {
    data: {},
  });

  const getDisplayAdsTemplateGroupDetails = (templateGroupId: string[], version: string = '1.2') => {
    const payload = {
      data: {
        templateGroupId,
        version,
      },
    };

    return recommendationService.post(campaignId, 'GetDisplayAdsTemplateGroupDetail', payload);
  };

  const getVideoAssetsRecommendation = (
    finalUrl: string,
    maxAdsCount: number = DEFAULT_MAX_ADS_COUNT,
    mock: boolean = false,
    recommendationType?: string,
    templateFilter?: any,
    brandKit?: any,
  ) => {
    const payload = {
      Url: finalUrl,
      Count: maxAdsCount, // optional, default 10.
      Mock: mock,
      RecommendationType: recommendationType,
      ...(templateFilter && { TemplateFilter: templateFilter }),
      ...(brandKit && { BrandKit: brandKit }),
    };

    const methodName = useClipchamp ? 'CreateVideoAssetsRecommendation?useClipchamp=true' : 'CreateVideoAssetsRecommendation';

    return recommendationService.post(campaignId, methodName, payload);
  };

  const editVideoAssetRecommendation = (
    ads: any,
    mock: boolean = false,
    styles?: any,
  ) => {
    const payload = {
      data: {
        ads: [ads],
        Mock: mock,
        ...(styles && { Styles: styles }),
      },
    };

    const methodName = useClipchamp ? 'EditVideoAssetRecommendation?useClipchamp=true' : 'EditVideoAssetRecommendation';

    return recommendationService.post(campaignId, methodName, payload);
  };

  const getVideoKeyFrameRecommendation = (
    data: any,
    mock: boolean = false,
  ) => {
    const payload = {
      data: {
        ...data,
        Mock: mock,
      },
    };

    return recommendationService.post(campaignId, 'EditKeyFrame', payload);
  };

  const getIsCustomerIdInExclusionList = () => new Promise<boolean>((resolve) => {
    resolve(pharmaCustomerIdsFromCustomerExclusionList.includes(serverContext.CurrentCustomer.Id));
  });

  const stopGetThemeAssetsRecommendation = () => recommendationService.stopPolling('GetThemeAssetsRecommendation');

  const stopGetAssetsRecommendation = () => recommendationService.stopPolling('GetAssetsRecommendation');

  const stopRefineTextAsset = () => recommendationService.stopPolling('RefineTextAsset');

  const stopGenerateImages = () => recommendationService.stopPolling('GenerateImages');

  const stopRefineImage = () => recommendationService.stopPolling('RefineImage');

  return {
    getThemeAssetsRecommendation,
    getMappedThemeAssetsRecommendation,
    getAssetsRecommendation,
    getMappedAssetsRecommendation,
    refineTextAsset,
    rewriteTextAsset,
    tryDifferentToneTextAsset,
    findSimilarTextAsset,
    generateImages,
    refineImage,
    getMappedDisplayAdsRecommendation,
    editMappedDisplayAdsRecommendation,
    getDisplayAdsTemplateDefinition,
    getDisplayAdsTemplateGroups,
    getDisplayAdsTemplateGroupDetails,
    getVideoAssetsRecommendation,
    editVideoAssetRecommendation,
    getVideoKeyFrameRecommendation,
    getIsCustomerIdInExclusionList,
    stopGetThemeAssetsRecommendation,
    stopGetAssetsRecommendation,
    stopRefineTextAsset,
    stopGenerateImages,
    stopRefineImage,
  };
};
